import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Timeline } from "primereact/timeline";
import { Skeleton } from "primereact/skeleton";
import { Fetch } from "../../../shared/Fetch";
import { safeFormatDate } from "../../../models/DateFormatMapper";
import { ILogin } from "../../../models/Login";

interface Props {
  visible: boolean;
  workstreamId: number | null;
  toast: any;
  onHide: () => void;
}

interface IHistoryItem {
  id: number;
  status: string;
  comment: string | null;
  added_By: string;
  added_On: string;
}

const statusMeta: Record<string, { color: string; icon: string }> = {
  Created: { color: "#64748B", icon: "pi-plus" },
  Assigned: { color: "#3B82F6", icon: "pi-user-plus" },
  Pending: { color: "#F59E0B", icon: "pi-clock" },
  Received: { color: "#8B5CF6", icon: "pi-inbox" },
  "Under Review": { color: "#F97316", icon: "pi-search" },
  "Documentation Pending": { color: "#EAB308", icon: "pi-file" },
  Completed: { color: "#22C55E", icon: "pi-check-circle" },
  Rejected: { color: "#EF4444", icon: "pi-times-circle" },
  Closed: { color: "#475569", icon: "pi-lock" },
};

const getInitials = (name: string) =>
  name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

const WorkStreamHistory = ({ visible, onHide, toast, workstreamId }: Props) => {
  const [data, setData] = useState<IHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
  const DateFormat = loginObj.date_Format;

  const loadHistory = async () => {
    if (!workstreamId) return;
    try {
      setLoading(true);
      const response = await Fetch("TenderWorkstream/GetWorkstreamStatusHistory", "GET", null, `workstreamId=${workstreamId}`, toast);
      if (response?.ok) {
        const result = await response.json();
        setData(result);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && workstreamId) {
      loadHistory();
    } else {
      setData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, workstreamId]);

  const markerTemplate = (item: IHistoryItem, index: number) => {
    const meta = statusMeta[item.status] || { color: "#94A3B8", icon: "pi-circle-fill" };
    const isLatest = index === 0;
    return (
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: isLatest ? "2.25rem" : "1.9rem", height: isLatest ? "2.25rem" : "1.9rem", borderRadius: "50%", background: `linear-gradient(135deg, ${meta.color}, ${meta.color}CC)`, color: "#fff", boxShadow: isLatest ? `0 0 0 5px ${meta.color}22, 0 2px 6px rgba(0,0,0,0.15)` : `0 0 0 4px rgba(0,0,0,0.04)`, zIndex: 1 }}>
        <i className={`pi ${meta.icon}`} style={{ fontSize: isLatest ? "0.95rem" : "0.8rem" }} />
      </span>
    );
  };

  const oppositeTemplate = (item: IHistoryItem) => (
    <div style={{ padding: "0.35rem 0.75rem", textAlign: "right", minWidth: "7rem" }}>
      <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>{safeFormatDate(item.added_On, "dd MMM yyyy", DateFormat)}</div>
      {/* <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{safeFormatDate(item.added_On, "hh:mm a", DateFormat)}</div> */}
    </div>
  );

  const contentTemplate = (item: IHistoryItem, index: number) => {
    const meta = statusMeta[item.status] || { color: "#94A3B8", icon: "pi-circle-fill" };
    const isLatest = index === 0;
    return (
      <div style={{ background: "#fff", border: `1px solid ${isLatest ? meta.color + "55" : "#E5E7EB"}`, borderLeft: `3px solid ${meta.color}`, borderRadius: "10px", padding: "0.9rem 1.1rem", marginBottom: "1.4rem", boxShadow: isLatest ? "0 4px 12px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.03)", maxWidth: "20rem" }}>
        <div className="flex align-items-center justify-content-between" style={{ marginBottom: item.comment || item.added_By ? "0.5rem" : 0 }}>
          <span style={{ fontWeight: 700, fontSize: "0.92rem", color: "#1E293B", letterSpacing: "0.01em" }}>{item.status}</span>
          {isLatest && <span style={{ fontSize: "0.65rem", fontWeight: 700, color: meta.color, background: meta.color + "14", padding: "0.15rem 0.5rem", borderRadius: "999px", letterSpacing: "0.03em" }}>LATEST</span>}
        </div>

        {item.comment && <p style={{ margin: "0 0 0.6rem", fontSize: "0.83rem", lineHeight: 1.5, color: "#475569", whiteSpace: "pre-wrap" }}>{item.comment}</p>}

        <div className="flex align-items-center gap-2" style={{ fontSize: "0.75rem", color: "#64748B" }}>
          <span style={{ width: "1.35rem", height: "1.35rem", borderRadius: "50%", background: "#F1F5F9", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.6rem", fontWeight: 700 }}>{getInitials(item.added_By)}</span>
          {item.added_By || "Unknown"}
        </div>
      </div>
    );
  };

  return (
    <Dialog header="Status History" visible={visible} modal style={{ width: "42vw" }} breakpoints={{ "960px": "70vw", "641px": "94vw" }} onHide={onHide}>
      {loading ? (
        <div className="flex flex-column gap-3" style={{ padding: "0.5rem 0" }}>
          <Skeleton width="100%" height="4.5rem" borderRadius="10px" />
          <Skeleton width="100%" height="4.5rem" borderRadius="10px" />
          <Skeleton width="100%" height="4.5rem" borderRadius="10px" />
        </div>
      ) : data.length === 0 ? (
        <div className="flex flex-column align-items-center justify-content-center" style={{ padding: "3rem 0", color: "#94A3B8" }}>
          <i className="pi pi-history" style={{ fontSize: "2.2rem", marginBottom: "0.6rem" }} />
          <span style={{ fontSize: "0.9rem" }}>No status history yet</span>
        </div>
      ) : (
        <Timeline value={data} align="left" marker={(item) => markerTemplate(item, data.indexOf(item))} content={(item) => contentTemplate(item, data.indexOf(item))} opposite={oppositeTemplate} className="workstream-history-timeline" />
      )}
    </Dialog>
  );
};

export default WorkStreamHistory;
